import React, { useRef, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  HMSRoomProvider,
  useHMSActions,
  useHMSStore,
  selectPeers,
  selectLocalPeer,
  selectIsConnectedToRoom,
  selectAudioTrackByPeerID
} from '@100mslive/hms-video-react';
import {
  FaVideo,
  FaVideoSlash,
  FaMicrophone,
  FaMicrophoneSlash,
  FaPhoneSlash,
  FaUsers,
  FaSpinner,
  FaPlay,
  FaUserTie,
  FaUserGraduate
} from 'react-icons/fa';
import axios from 'axios';
import './LiveClass.css';

const LiveClassContent = () => {
  const { user } = useSelector((state) => state.auth);
  const hmsActions = useHMSActions();
  const peers = useHMSStore(selectPeers);
  const localPeer = useHMSStore(selectLocalPeer);
  const isConnected = useHMSStore(selectIsConnectedToRoom);

  const [isJoining, setIsJoining] = useState(false);
  const [userName, setUserName] = useState(user?.name || '');
  const [role, setRole] = useState(user?.role === 'instructor' || user?.role === 'admin' ? 'host' : 'viewer');
  const [code, setCode] = useState('ycz-zscb-oox'); // Default 100ms room code for host

  const canBeHost = user?.role === 'instructor' || user?.role === 'admin';

  const joinRoom = async () => {
    if (!userName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    if (!code.trim()) {
      toast.error('Please enter the room code');
      return;
    }
    setIsJoining(true);
    try {
      // Get token from backend
      const response = await axios.get(`/api/v1/live/token?role=${role}&userName=${encodeURIComponent(userName)}&code=${encodeURIComponent(code)}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const { token } = response.data.data;

      // Join the room
      await hmsActions.join({
        userName,
        authToken: token
      });

      toast.success('Successfully joined the live class!');
    } catch (error) {
      console.error('Error joining room:', error);
      toast.error(error.response?.data?.message || 'Failed to join the live class');
    } finally {
      setIsJoining(false);
    }
  };

  const leaveRoom = async () => {
    try {
      await hmsActions.leave();
      toast.info('Left the live class');
    } catch (error) {
      console.error('Error leaving room:', error);
    }
  };

  const toggleVideo = async () => {
    try {
      await hmsActions.setLocalVideoEnabled(!localPeer?.videoTrack);
    } catch (error) {
      console.error('Error toggling video:', error);
    }
  };

  const toggleAudio = async () => {
    try {
      await hmsActions.setLocalAudioEnabled(!localPeer?.audioTrack);
    } catch (error) {
      console.error('Error toggling audio:', error);
    }
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white container-rounded-lg shadow-lg p-6">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
              <FaVideo className="text-white text-2xl" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Live Class</h1>
            <p className="text-gray-600">
              {canBeHost ? 'Start or join a live class session' : 'Join the live class session'}
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Your Name
              </label>
              <input
                type="text"
                value={userName}
                onChange={(e) => setUserName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 container-rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter your name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Room Code
              </label>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 container-rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Enter 100ms room code"
              />
            </div>

            {canBeHost && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Role
                </label>
                <select
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 container-rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="host">Host (Instructor)</option>
                  <option value="viewer">Viewer (Student)</option>
                </select>
              </div>
            )}

            <button
              onClick={joinRoom}
              disabled={isJoining || !userName.trim()}
              className="w-full bg-primary text-white py-3 px-4 container-rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isJoining ? (
                <>
                  <FaSpinner className="animate-spin mr-2" />
                  Joining...
                </>
              ) : (
                <>
                  <FaPlay className="mr-2" />
                  {role === 'host' ? 'Start Class' : 'Join Class'}
                </>
              )}
            </button>
          </div>

          <div className="mt-6 text-center">
            <div className="flex items-center justify-center text-sm text-gray-500">
              {role === 'host' ? (
                <>
                  <FaUserTie className="mr-2 text-blue-500" />
                  You'll join as an instructor
                </>
              ) : (
                <>
                  <FaUserGraduate className="mr-2 text-green-500" />
                  You'll join as a student
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <div className="bg-gray-800 text-white p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <FaVideo className="mr-2" />
            <h1 className="text-xl font-bold">Live Class</h1>
            <span className="ml-4 px-2 py-1 bg-red-500 text-white text-xs rounded-full">
              LIVE
            </span>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm">
              <FaUsers className="inline mr-1" />
              {peers.length} participants
            </span>
            <button
              onClick={leaveRoom}
              className="bg-red-600 hover:bg-red-700 px-4 py-2 container-rounded-md flex items-center"
            >
              <FaPhoneSlash className="mr-2" />
              Leave
            </button>
          </div>
        </div>
      </div>

      {/* Video Grid */}
      <div className="p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {peers.map((peer) => (
            <div key={peer.id} className="relative bg-gray-800 container-rounded-lg overflow-hidden aspect-video">
              {peer.videoTrack ? (
                <video
                  ref={(node) => {
                    if (node) hmsActions.attachVideo(peer.videoTrack, node);
                  }}
                  autoPlay
                  muted={peer.isLocal}
                  playsInline
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-gray-600 rounded-full flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl text-white">
                        {peer.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <p className="text-white text-sm">{peer.name}</p>
                    {peer.isLocal && <p className="text-gray-400 text-xs">(You)</p>}
                  </div>
                </div>
              )}
              <PeerAudio peerId={peer.id} isLocal={peer.isLocal} />
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="fixed bottom-0 left-0 right-0 bg-gray-800 p-4">
        <div className="flex items-center justify-center space-x-4">
          <button
            onClick={toggleAudio}
            className={`p-3 rounded-full ${
              localPeer?.audioTrack ? 'bg-gray-600 hover:bg-gray-700' : 'bg-red-600 hover:bg-red-700'
            } text-white`}
          >
            {localPeer?.audioTrack ? <FaMicrophone /> : <FaMicrophoneSlash />}
          </button>
          <button
            onClick={toggleVideo}
            className={`p-3 rounded-full ${
              localPeer?.videoTrack ? 'bg-gray-600 hover:bg-gray-700' : 'bg-red-600 hover:bg-red-700'
            } text-white`}
          >
            {localPeer?.videoTrack ? <FaVideo /> : <FaVideoSlash />}
          </button>
          <button
            onClick={leaveRoom}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-full flex items-center"
          >
            <FaPhoneSlash className="mr-2" />
            Leave Class
          </button>
        </div>
      </div>
    </div>
  );
};

const LiveClass = () => {
  return (
    <HMSRoomProvider>
      <LiveClassContent />
    </HMSRoomProvider>
  );
};

const PeerAudio = ({ peerId, isLocal }) => {
  const audioRef = useRef(null);
  const audioTrack = useHMSStore(selectAudioTrackByPeerID(peerId));
  useEffect(() => {
    if (audioRef.current && audioTrack) {
      audioRef.current.srcObject = audioTrack.stream;
    }
  }, [audioTrack]);
  return <audio ref={audioRef} autoPlay muted={isLocal} />;
};

export default LiveClass;